import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { QrCodeStats, ReferrerCount, ScanEvent } from '../api/types';
import { formatDate, formatDateTime, formatTimestamp } from '../format';
import { DataGrid } from './DataGrid';

export function StatsPanel({ stats, scans }: { stats: QrCodeStats; scans: ScanEvent[] }) {
  const { t } = useTranslation();
  /**
   * The days worth drawing: the first scan onwards, because twenty-nine empty columns say nothing about a code
   * first scanned yesterday. Cut on the date of the first scan rather than on the leading run of zeros - a code
   * scanned before this window opened, then quiet for a fortnight, has empty days that are part of its history.
   */
  const days = useMemo(() => {
    if (!stats.firstScanAt) {
      return stats.daily;
    }

    const first = formatDate(stats.firstScanAt);
    const from = stats.daily.findIndex((day) => day.date >= first);

    return from > 0 ? stats.daily.slice(from) : stats.daily;
  }, [stats.daily, stats.firstScanAt]);

  const peak = Math.max(1, ...days.map((day) => day.count));

  // The vertical scale: the busiest day, the middle, and nothing. The middle is dropped when it would repeat
  // one of the other two.
  const middle = Math.round(peak / 2);
  const scale = [peak, ...(middle > 0 && middle < peak ? [middle] : []), 0];

  const none = t('common.none');

  const referrerColumns = useMemo<ColDef<ReferrerCount>[]>(
    () => [
      { field: 'referrer', headerName: t('stats.referrer'), flex: 3, minWidth: 180, cellClass: 'mono' },
      { field: 'count', headerName: t('qrCode.scans'), type: 'numericColumn', width: 110 },
    ],
    [t],
  );

  const scanColumns = useMemo<ColDef<ScanEvent>[]>(
    () => [
      {
        field: 'scannedAt',
        headerName: t('stats.when'),
        flex: 2,
        minWidth: 160,
        valueFormatter: ({ value }) => formatTimestamp(value, none),
      },
      {
        field: 'userAgent',
        headerName: t('stats.userAgent'),
        flex: 3,
        minWidth: 160,
        cellClass: 'mono',
        cellRenderer: ({ value }: ICellRendererParams<ScanEvent>) => (
          <span className="text-truncate cell-truncate" title={value ?? undefined}>
            {value ?? t('common.none')}
          </span>
        ),
      },
      {
        field: 'referrer',
        headerName: t('stats.referrer'),
        flex: 2,
        minWidth: 140,
        cellClass: 'mono',
        cellRenderer: ({ value }: ICellRendererParams<ScanEvent>) => (
          <span className="text-truncate cell-truncate" title={value ?? undefined}>
            {value ?? t('common.none')}
          </span>
        ),
      },
    ],
    [t, none],
  );

  const tiles = [
    { icon: 'pi-chart-line', value: stats.totalScans, label: t('stats.totalScans') },
    { icon: 'pi-clock', value: stats.scansLast24Hours, label: t('stats.last24Hours') },
    { icon: 'pi-calendar', value: stats.scansLast7Days, label: t('stats.last7Days') },
    { icon: 'pi-calendar-clock', value: stats.scansLast30Days, label: t('stats.last30Days') },
    // The same tiles, holding a moment rather than a count: a date needs two columns where a number does not.
    {
      icon: 'pi-flag',
      value: formatDateTime(stats.firstScanAt, none),
      label: t('stats.firstScan'),
      title: formatTimestamp(stats.firstScanAt, none),
      wide: true,
    },
    {
      icon: 'pi-history',
      value: formatDateTime(stats.lastScanAt, none),
      label: t('stats.lastScan'),
      title: formatTimestamp(stats.lastScanAt, none),
      wide: true,
    },
  ];

  return (
    <>
      <div className="stat-grid">
        {tiles.map((tile) => (
          <div className={tile.wide ? 'stat stat--wide' : 'stat'} key={tile.label} title={tile.title}>
            <div className="value">
              <i className={`pi ${tile.icon} me-2`} aria-hidden="true" />
              {tile.value}
            </div>
            <div className="label">{tile.label}</div>
          </div>
        ))}
      </div>

      <h3>{t('stats.scansPerDay')}</h3>
      <div className="chart-wrap">
        {/* Read off the bars, so the height of one means something without hovering it. */}
        <div className="chart-scale" aria-hidden="true">
          {scale.map((value) => (
            <span key={value}>{value}</span>
          ))}
        </div>

        <div
          className="chart"
          role="img"
          aria-label={t('stats.dailyChartLabel', { count: days.length })}
        >
          {days.map((day) => (
            <div
              key={day.date}
              className={day.count === 0 ? 'bar empty' : 'bar'}
              style={{ height: `${Math.max(2, (day.count / peak) * 100)}%` }}
              /* Read by CSS into a bubble over the bar, rather than the browser's own tooltip: that one takes a
                 second to appear, by which time the pointer has moved to the next day. */
              data-count={t('stats.scanCount', { count: day.count })}
              data-date={day.date}
            />
          ))}
        </div>

        <div className="chart-axis">
          <span>{days[0]?.date}</span>
          <span>{days[days.length - 1]?.date}</span>
        </div>
      </div>

      {stats.topReferrers.length > 0 && (
        <>
          <h3>{t('stats.topReferrers')}</h3>
          <DataGrid
            rows={stats.topReferrers}
            columns={referrerColumns}
            getRowId={(referrer) => referrer.referrer}
            label={t('stats.topReferrers')}
            emptyMessage={t('stats.neverScanned')}
            rowHeight={40}
          />
        </>
      )}

      <h3>{t('stats.recentScans')}</h3>
      {scans.length === 0 ? (
        <div className="alert alert-info d-flex gap-2" role="status">
          <i className="pi pi-info-circle mt-1" aria-hidden="true" />
          <span>{t('stats.neverScanned')}</span>
        </div>
      ) : (
        <DataGrid
          rows={scans}
          columns={scanColumns}
          // Scans carry no id of their own; two in the same millisecond would be the same event anyway.
          getRowId={(scan) => `${scan.scannedAt}-${scan.userAgent ?? ''}-${scan.referrer ?? ''}`}
          label={t('stats.recentScans')}
          emptyMessage={t('stats.neverScanned')}
          rowHeight={40}
        />
      )}
    </>
  );
}
